import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Package, TrendingUp, AlertTriangle, ShoppingCart, Truck, 
  BarChart3, Bot, Bell, LogOut, Menu, X, RefreshCw,
  DollarSign, ClipboardList, Users, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComprasDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    lowStock: 0,
    pendingOrders: 0,
    totalProducts: 0,
    monthlySpend: 0
  });
  const [lowStockItems, setLowStockItems] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch products for inventory
      const prodRes = await fetch(`${API_URL}/api/products`, { headers: getAuthHeaders() });
      const products = await prodRes.json();
      
      // Calculate low stock
      const lowStock = products.filter(p => (p.stock || 0) <= (p.min_stock || 10));
      setLowStockItems(lowStock.slice(0, 10));
      
      setStats({
        lowStock: lowStock.length,
        pendingOrders: 12, // Mock
        totalProducts: products.length,
        monthlySpend: 45000 // Mock
      });
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const runAISuggestions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/ops/purchase-suggestions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({})
      });
      const data = await res.json();
      setAiSuggestions(data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { id: 'overview', label: 'Resumen', icon: BarChart3 },
    { id: 'inventory', label: 'Inventario', icon: Package },
    { id: 'orders', label: 'Órdenes de Compra', icon: ClipboardList },
    { id: 'suppliers', label: 'Proveedores', icon: Users },
    { id: 'ai', label: 'AI Sugerencias', icon: Bot },
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-amber-900 text-white transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-amber-800">
          <div className="flex items-center justify-between">
            {sidebarOpen && <img src="/logo.png" alt="Lunch & Roll" className="h-8" />}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-amber-800 rounded-lg">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id 
                  ? 'bg-amber-700 text-white' 
                  : 'text-amber-200 hover:bg-amber-800'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-amber-800">
          {sidebarOpen && (
            <div className="mb-4">
              <p className="text-amber-300 text-sm">{user?.name}</p>
              <p className="text-amber-400 text-xs">{user?.email}</p>
            </div>
          )}
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2 text-amber-200 hover:bg-amber-800 rounded-lg">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Salir</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">
              {navItems.find(n => n.id === activeTab)?.label}
            </h2>
            <p className="text-stone-500">Panel de Compras</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <span className="text-red-600 text-sm font-medium">Crítico</span>
                </div>
                <p className="text-3xl font-bold text-stone-800">{stats.lowStock}</p>
                <p className="text-stone-500 text-sm">Productos sin stock</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <ClipboardList className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">{stats.pendingOrders}</p>
                <p className="text-stone-500 text-sm">Órdenes pendientes</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">{stats.totalProducts}</p>
                <p className="text-stone-500 text-sm">Productos totales</p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-stone-800">${stats.monthlySpend.toLocaleString()}</p>
                <p className="text-stone-500 text-sm">Gasto mensual</p>
              </div>
            </div>

            {/* Low Stock Alert */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
              <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Productos con Stock Bajo
              </h3>
              {lowStockItems.length > 0 ? (
                <div className="space-y-3">
                  {lowStockItems.map(item => (
                    <div key={item.product_id} className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <img src={item.image_url} alt={item.name} className="w-10 h-10 rounded-lg object-cover" />
                        <div>
                          <p className="font-medium text-stone-800">{item.name}</p>
                          <p className="text-sm text-stone-500">Stock: {item.stock || 0}</p>
                        </div>
                      </div>
                      <Button size="sm" className="bg-red-600 hover:bg-red-700">
                        Ordenar
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-stone-500 text-center py-8">No hay productos con stock bajo</p>
              )}
            </div>
          </div>
        )}

        {/* AI Suggestions Tab */}
        {activeTab === 'ai' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Bot className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">AI de Compras</h3>
                  <p className="text-amber-100">Sugerencias inteligentes basadas en tu inventario</p>
                </div>
              </div>
              <Button 
                onClick={runAISuggestions}
                className="bg-white text-amber-700 hover:bg-amber-50"
                disabled={loading}
              >
                {loading ? 'Analizando...' : 'Generar Sugerencias'}
              </Button>
            </div>

            {aiSuggestions && (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
                <h4 className="font-semibold text-stone-800 mb-4">Resultado del Análisis</h4>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-red-50 p-4 rounded-xl">
                    <p className="text-red-600 text-sm">Stock Crítico</p>
                    <p className="text-2xl font-bold text-red-700">{aiSuggestions.summary?.critical || 0}</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl">
                    <p className="text-orange-600 text-sm">Stock Bajo</p>
                    <p className="text-2xl font-bold text-orange-700">{aiSuggestions.summary?.low || 0}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-xl">
                    <p className="text-green-600 text-sm">Saludable</p>
                    <p className="text-2xl font-bold text-green-700">{aiSuggestions.summary?.healthy || 0}</p>
                  </div>
                </div>
                <div className="bg-stone-50 rounded-xl p-4">
                  <pre className="text-sm text-stone-700 whitespace-pre-wrap">
                    {aiSuggestions.ai_suggestions}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Other tabs placeholder */}
        {['inventory', 'orders', 'suppliers'].includes(activeTab) && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-12 text-center">
            <Package className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-700 mb-2">
              {navItems.find(n => n.id === activeTab)?.label}
            </h3>
            <p className="text-stone-500">Esta sección está en desarrollo</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ComprasDashboard;
